import React, { useEffect, useMemo, useState } from "react";
import { displayLabel, employeeDisplayName } from "../../../utils/displayLabel";
import { createPortal } from "react-dom";
import Select from "react-select";
import { X } from "lucide-react";
import PolicyApplicabilityStepSection from "../policy-wizard/PolicyApplicabilityStepSection";
import { filterEmployeesByApplicability } from "./runPayrollUtils";

/**
 * Action chooser: All (default) / Selected / Group-wise over a given employee pool.
 */
export default function PayrollActionChooserModal({
  darkMode,
  open,
  title,
  subtitle,
  employees = [],
  masterLists = {},
  confirmLabel = "Confirm",
  onClose,
  onConfirm,
  extraContent = null,
  confirmDisabled = false,
}) {
  const [mode, setMode] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupPolicy, setGroupPolicy] = useState({ allEmployees: true });

  useEffect(() => {
    if (!open) return;
    setMode("all");
    setSelectedIds([]);
    setGroupPolicy({ allEmployees: true });
  }, [open]);

  const options = useMemo(
    () =>
      (employees || []).map((e) => ({
        value: e.id,
        label: `${displayLabel(e.empCode || e.id, "")} — ${employeeDisplayName(e)}`.trim(),
      })),
    [employees]
  );

  const resolved = useMemo(() => {
    if (mode === "employees") {
      const set = new Set(selectedIds.map(Number));
      return (employees || []).filter((e) => set.has(Number(e.id)));
    }
    if (mode === "group") {
      return filterEmployeesByApplicability(employees, groupPolicy);
    }
    return employees || [];
  }, [mode, selectedIds, employees, groupPolicy]);

  const selectedOptions = options.filter((o) => selectedIds.includes(o.value));
  const border = darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]";

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100055] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`flex max-h-[calc(100vh-6rem)] w-full max-w-lg flex-col overflow-hidden rounded-xl border shadow-xl ${
          darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div
          className={`flex shrink-0 items-start justify-between gap-2 border-b px-4 py-3 ${
            darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <div>
            <h3 className="text-sm font-semibold">{title}</h3>
            {subtitle && (
              <p className={`mt-0.5 text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className={`rounded p-1.5 ${darkMode ? "hover:bg-[hsl(var(--ds-muted))]" : "hover:bg-[hsl(var(--ds-muted))]"}`}
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 text-sm">
          <div className="flex flex-wrap gap-4">
            {[
              ["all", "All"],
              ["employees", "Selected"],
              ["group", "Group-wise"],
            ].map(([val, label]) => (
              <label key={val} className="flex items-center gap-2 text-xs">
                <input
                  type="radio"
                  name="payroll-action-chooser"
                  checked={mode === val}
                  onChange={() => setMode(val)}
                />
                {label}
              </label>
            ))}
          </div>

          {mode === "employees" && (
            <Select
              isMulti
              isSearchable
              placeholder="Search and select employees…"
              options={options}
              value={selectedOptions}
              onChange={(opts) => setSelectedIds((opts || []).map((o) => o.value))}
              className="text-sm"
              classNamePrefix="payroll-action-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 100060 }),
                control: (base) => ({
                  ...base,
                  minHeight: 36,
                  fontSize: 12,
                  background: darkMode ? "#2C2C2C" : base.background,
                  borderColor: darkMode ? "#555" : base.borderColor,
                }),
              }}
            />
          )}

          {mode === "group" && (
            <div className={`rounded border p-3 ${border}`}>
              <PolicyApplicabilityStepSection
                darkMode={darkMode}
                policy={groupPolicy}
                setPolicy={setGroupPolicy}
                branchesList={masterLists.branchesList || []}
                departmentsList={masterLists.departmentsList || []}
                designationsList={masterLists.designationsList || []}
                gradesList={masterLists.gradesList || []}
                categoriesList={masterLists.categoriesList || []}
                subDepartmentsList={masterLists.subDepartmentsList || []}
                title="Group filters"
                allEmployeesLabel="All in this list"
              />
            </div>
          )}

          <div
            className={`rounded-lg border px-3 py-2 text-xs ${
              darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
            }`}
          >
            <strong className="tabular-nums">{resolved.length}</strong> of{" "}
            <strong className="tabular-nums">{employees.length}</strong> employee(s) will be included
          </div>

          {mode === "all" && employees.length > 0 && (
            <div
              className={`max-h-40 overflow-auto rounded border text-[11px] ${border}`}
            >
              <ul className="divide-y divide-[hsl(var(--ds-border))] dark:divide-[#333]">
                {employees.slice(0, 40).map((e) => (
                  <li key={e.id} className="px-2 py-1">
                    {displayLabel(e.empCode, "")} — {employeeDisplayName(e)}
                    {e.netPay != null ? (
                      <span className="float-right tabular-nums opacity-70">
                        {Number(e.netPay).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </span>
                    ) : null}
                  </li>
                ))}
                {employees.length > 40 && (
                  <li className="px-2 py-1 text-[hsl(var(--ds-muted-fg))]">…and {employees.length - 40} more</li>
                )}
              </ul>
            </div>
          )}

          {extraContent}
        </div>

        <div
          className={`flex shrink-0 justify-end gap-2 border-t px-4 py-3 ${
            darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <button type="button" className="rounded border px-3 py-1.5 text-xs" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!resolved.length || confirmDisabled}
            className="rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            onClick={() =>
              onConfirm({
                mode,
                employeeIds: resolved.map((e) => e.id),
                count: resolved.length,
              })
            }
          >
            {confirmLabel} ({resolved.length})
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
